import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Button, LinkButton } from '../ui/Button';
import { TextArea, TextField } from '../ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL, SERVICE_AREA } from '../../data/site';
import { logConversionSkipped, trackEvent, trackLeadConversion } from '../../utils/analytics';
import { MAX_IMAGE_MB, validateImage } from '../../services/imageService';
import { telHref } from '../../utils/format';
import { FacebookLink } from '../site/FacebookLink';
const ease = [0.23, 1, 0.32, 1] as const;
const emptyForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  description: ''
};
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
interface Selected {
  file: File;
  preview: string;
  /** Udfyldt hvis filen ikke kan bruges — blokerer indsendelse indtil den fjernes. */
  problem: string | null;
}
export function ContactSection() {
  const {
    createEnquiry,
    settings
  } = useAdminData();
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState<Selected[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  /** Id på en påbegyndt, ufærdig henvendelse — genbruges ved nyt forsøg. */
  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const invalid = selected.filter(item => item.problem);
  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Skriv dit navn.';
    if (form.phone.replace(/[^\d]/g, '').length < 8) next.phone = 'Skriv et gyldigt telefonnummer.';
    if (form.email && !emailPattern.test(form.email.trim())) next.email = 'Skriv en gyldig e-mailadresse.';
    if (!form.description.trim()) next.description = 'Beskriv kort opgaven.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function removeImage(index: number) {
    setSubmitError('');
    setSelected(current => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  }
  return <section id="kontakt" className="scroll-mt-24" data-id="element-655" data-mp="2x1skgz3kz7id">
      <div className="mx-auto max-w-content px-5 py-16 md:py-24 lg:px-8" data-id="element-656" data-mp="3brgo46z8ii99">
        <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16" data-id="element-657" data-mp="36ty9bzyjd8tc">
          <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-658" data-mp="3kt7px0qnm4xu">
            <h2 className="font-display text-[clamp(2rem,5.5vw,3.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-659" data-mp="3kso8dl4x1ikc">
              Hvad skal du have lavet?
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-ink-soft" data-id="element-660" data-mp="1qg82ilg186g0">
              Beskriv kort din opgave og send gerne et par billeder. Så kan jeg bedre vurdere, hvad
              der skal til. Du kan også bare ringe.
            </p>
            <p className="mt-4 max-w-md border-l-2 border-accent pl-5 text-[16px] leading-relaxed text-ink-soft" data-id="element-661" data-mp="10dzndf2lbez">
              Du behøver ikke vide præcis, hvad opgaven kræver. Beskriv problemet, som du ser det, og
              send gerne et par billeder – så tager vi den derfra.
            </p>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-ink-muted" data-id="element-662" data-mp="3sacp5uao48e6">
              Jeg kører primært opgaver i {SERVICE_AREA}.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-663" data-mp="3jcu0mfxtfbxj">
              {settings.phone && <LinkButton href={telHref(settings.phone)} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-664" data-mp="35e2kyp3b1i7a">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-665" data-mp="13j2063otemez" />
                  {settings.phone}
                </LinkButton>}
              <LinkButton href={`mailto:${CONTACT_EMAIL}`} variant="secondary" size="lg" className="sm:w-auto lg:w-full" data-id="element-666" data-mp="iw3q2ylcjt4p">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-667" data-mp="8wmpgvkyc5fl" />
                {CONTACT_EMAIL}
              </LinkButton>
            </div>

            <FacebookLink label="Se LYKKEVÆRK på Facebook" className="mt-5 text-[15px]" data-id="element-668" data-mp="40ntaltdtj4m" />

            <p className="mt-8 max-w-md border-l-2 border-line pl-5 text-[15px] leading-relaxed text-ink-muted" data-id="element-669" data-mp="3w3ot9cvpqq6m">
              Du får en pris eller et tilbud, inden jeg går i gang — så ved du, hvad din opgave
              koster. Registreret med CVR {settings.cvr}.
            </p>
          </div>

          {sent ? <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.28,
          ease
        }} role="status" className="flex flex-col items-start rounded-card border border-line bg-white p-8 md:p-12" data-id="element-670" data-mp="3ced9esp0wdq5">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-671" data-mp="1rvsx53n6s0ej">
                <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-672" data-mp="1wwqfbg874ug1" />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-673" data-mp="21tgw65rtru2i">
                Tak for din henvendelse.
              </h3>
              <p className="mt-3 text-[17px] text-ink-soft" data-id="element-674" data-mp="195l115hwma7u">
                LYKKEVÆRK vender tilbage hurtigst muligt.
              </p>
              <Button variant="secondary" size="md" className="mt-8" onClick={() => setSent(false)} data-id="element-675" data-mp="1e3w7r7cwk4lu">
                Send en opgave mere
              </Button>
              <p className="mt-6 text-[15px] text-ink-muted" data-id="element-676" data-mp="lg9zhlsqcbsn">
                Har du travlt med opgaven, må du gerne bare ringe.
              </p>
            </motion.div> : <form noValidate className="rounded-card border border-line bg-white p-6 md:p-9" onSubmit={async event => {
          event.preventDefault();
          if (submitting) return;
          if (!validate()) return;
          if (invalid.length > 0) {
            setSubmitError(invalid[0].problem ?? '');
            return;
          }
          setSubmitting(true);
          setSubmitError('');
          const result = await createEnquiry({
            ...form,
            files: selected.map(item => item.file),
            draftId
          });
          setSubmitting(false);
          if (!result.ok) {
            // Samme dokument genbruges ved næste forsøg, så der ikke opstår dubletter.
            setDraftId(result.draftId);
            setSubmitError(result.error ?? 'Henvendelsen kunne ikke sendes. Prøv igen, eller ring til os i stedet.');
            logConversionSkipped(result.error ?? 'indsendelsen fejlede');
            return;
          }

          // Herfra er henvendelsen oprettet, alle billeder uploadet, stierne
          // gemt og statussen sat til "Ny henvendelse".
          void trackEvent('generate_lead', {
            lead_source: 'website_contact_form',
            form_name: 'contact_form'
          });
          // Google Ads: én fuldført henvendelse = én konvertering.
          if (result.draftId) trackLeadConversion(result.draftId);
          selected.forEach(item => URL.revokeObjectURL(item.preview));
          setSelected([]);
          setForm(emptyForm);
          setErrors({});
          setDraftId(undefined);
          setSent(true);
        }} data-id="element-677" data-mp="qd0c1z5bsp9b">
              <p className="mb-5 text-sm text-ink-muted" data-id="element-678" data-mp="90wvtx05i2p3">
                Felter markeret med <span className="text-accent" data-id="element-679" data-mp="253i4jtpsdqk1">*</span> skal udfyldes.
              </p>

              <div className="grid gap-5 sm:grid-cols-2" data-id="element-680" data-mp="20cjlimdeyed3">
                <TextField id="navn" label="Navn" required autoComplete="name" placeholder="Dit navn" value={form.name} error={errors.name} onChange={event => setForm({
              ...form,
              name: event.target.value
            })} data-id="element-681" data-mp="20ayqkvn6step" />
                <TextField id="telefon" label="Telefonnummer" required type="tel" inputMode="tel" autoComplete="tel" placeholder="12 34 56 78" value={form.phone} error={errors.phone} onChange={event => setForm({
              ...form,
              phone: event.target.value
            })} data-id="element-682" data-mp="2j3sv0vn6p34o" />
                <TextField id="email" label="E-mail" type="email" autoComplete="email" placeholder="navn@gmail.com" value={form.email} error={errors.email} onChange={event => setForm({
              ...form,
              email: event.target.value
            })} data-id="element-683" data-mp="ypaekmrwbtuj" />
                <TextField id="omraade" label="Adresse eller område" autoComplete="street-address" placeholder="Fx Grenaa, Rønde eller Ebeltoft" value={form.address} onChange={event => setForm({
              ...form,
              address: event.target.value
            })} data-id="element-684" data-mp="3o3r349pcfnyw" />
              </div>

              <TextArea id="beskrivelse" label="Beskrivelse af opgaven" required className="mt-5" placeholder="Fortæl kort hvad der skal laves." value={form.description} error={errors.description} onChange={event => setForm({
            ...form,
            description: event.target.value
          })} data-id="element-685" data-mp="aumgjvy0xzqz" />

              <div className="mt-5" data-id="element-686" data-mp="34fonjxkc13tu">
                <p className="mb-1.5 text-sm font-medium text-ink" data-id="element-687" data-mp="39c5knh9iesj2">Upload billeder</p>
                <label htmlFor="billeder" className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-688" data-mp="3ebi8a487g3mp">
                  <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-689" data-mp="3s2wqi8uag0sp" />
                  <span className="text-[15px] font-medium text-ink" data-id="element-690" data-mp="x549kjmt8b6">Vælg billeder</span>
                  <span className="text-sm text-ink-muted" data-id="element-691" data-mp="5whzasknplou">
                    Valgfrit — billeder op til {MAX_IMAGE_MB} MB
                  </span>
                  <input id="billeder" type="file" multiple accept="image/*" className="sr-only" disabled={submitting} onChange={event => {
                const input = event.target;
                const picked = Array.from(input.files ?? []).map(file => ({
                  file,
                  preview: URL.createObjectURL(file),
                  problem: validateImage(file)
                }));
                setSelected(current => [...current, ...picked]);
                setSubmitError('');
                input.value = '';
              }} data-id="element-692" data-mp="9xtgc9pgsylf" />
                </label>

                {invalid.length > 0 && <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-693" data-mp="2wul27fij8ncu">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-694" data-mp="q62ky6h26ib2" />
                    <span data-id="element-695" data-mp="u7eakbzxn3j9">
                      {invalid.map(item => <span key={item.file.name} className="block" data-id="element-696" data-mp="u9icwpi6nuhf">
                          {item.problem}
                        </span>)}
                    </span>
                  </p>}

                {selected.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-697" data-mp="6ga0t8khycty">
                    {selected.map((item, index) => <li key={`${item.file.name}-${index}`} className={`overflow-hidden rounded-xl border ${item.problem ? 'border-danger' : 'border-line'}`} data-id="element-698" data-mp="bejypqxlvrj9" data-mpi={index}>
                        <div className="relative" data-id="element-699" data-mp="1rt3bi478vfdo">
                          <img src={item.preview} alt={item.file.name} className="aspect-square w-full object-cover" data-id="element-700" data-mp="1wuj0268ms40t" />
                          <button type="button" onClick={() => removeImage(index)} aria-label={`Fjern billede ${item.file.name}`} className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-701" data-mp="220o4wlk2x4kt">
                            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-702" data-mp="2ypvoho1513m3" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2" data-id="element-703" data-mp="2uoa3tgid3l1c">
                          <span className={`truncate text-xs ${item.problem ? 'text-danger' : 'text-ink-muted'}`} data-id="element-704" data-mp="14c4akcbrzxku">
                            {item.file.name}
                          </span>
                          <button type="button" onClick={() => removeImage(index)} className="shrink-0 text-xs font-medium text-accent hover:text-accent-deep" data-id="element-705" data-mp="16pzleddk1k9y">
                            Fjern
                          </button>
                        </div>
                      </li>)}
                  </ul>}
              </div>

              {Object.keys(errors).length > 0 && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-706" data-mp="s0v0upjaf3yo">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-707" data-mp="3vidbf4qg2p2s" />
                  Tjek de markerede felter, før du sender.
                </p>}

              {submitError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-708" data-mp="3swcldfetp0gk">
                  <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-709" data-mp="3iyolyc69v4s2" />
                  {submitError}
                </p>}

              <Button type="submit" size="lg" className="mt-7 w-full text-lg" disabled={submitting || invalid.length > 0} data-id="element-710" data-mp="3ezpf2m0zcxxz">
                {submitting ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-711" data-mp="309i2uo1kgb5q" />
                    {selected.length > 0 ? 'Sender og uploader billeder …' : 'Sender …'}
                  </> : 'Send din opgave'}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
}